import { REPORT_TEMPLATE, type IReportCardInfo, type IChatItem, type TechSchemeCardData } from '@/types';
import type { BaseCardService } from './abstract-base-card-service';
import { BaseCardServiceImpl, type BaseReportCardData } from './abstract-base-card-service';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { ReportApiService } from '@/infrastructure/api-functions/report-api-service';
import { TechSchemeApiService } from '@/infrastructure/api-functions/tech-scheme-api-service';
import _ from 'lodash'

export const TechSchemeService = Symbol('TechSchemeService');

export interface TechSchemeService extends BaseCardService<TechSchemeCardData> {
    getAndOpenSolutionDetail(index: number, projectId: string): Promise<void>;
    initProjectId(id: string): void;
}

@injectable()
export class TechSchemeServiceImpl extends BaseCardServiceImpl<TechSchemeCardData> implements TechSchemeService {
    _modelData = {
        isLoading: false,
        isFailed: false,
        isSuccess: false,
        data: undefined,
    };

    templateType = REPORT_TEMPLATE.TECHNICAL_SCHEME_REVIEW;

    private _projectId = ''

    constructor(
        @inject(TechSchemeApiService) private _techSchemeApiService: TechSchemeApiService,
        @inject(ReportApiService) _reportApiService: ReportApiService
    ) {
        super(
            _reportApiService
        );
    }

    get projectId() {
        return this._projectId
    }

    private _openFirstSolutionDetail(solutionList?: TechSchemeCardData) {
        if (!solutionList || !solutionList.length) return;
        this.getAndOpenSolutionDetail(0, this._projectId)
    }

    private _generateTechSchemeData(cardData: BaseReportCardData<IReportCardInfo>) {
        const cardInfo = cardData.data?.[REPORT_TEMPLATE.TECHNICAL_SCHEME_REVIEW];
        const gptFirst = cardInfo?.SOLUTION_GPT_FIRST
        const data = gptFirst?.map((solution: IChatItem) => ({
            ...solution,
            loading: false,
            isExpand: false,
            detail: undefined,
        }))
        return data
    }

    updateFetchData(getUpdateValue: (v: BaseReportCardData<IReportCardInfo>) => BaseReportCardData<IReportCardInfo>): void {
        const updatedValue = getUpdateValue(_.omit(this.modelData, 'data'));

        const dataHaveFromUpdated = _.has(updatedValue, 'data')

        const data = this._generateTechSchemeData(updatedValue)

        if (dataHaveFromUpdated) {
            this.update(modelData => ({
                ...modelData,
                ...updatedValue,
                data,
            }));

            /**
             * 默认打开第一个方案详情
             */
            this._openFirstSolutionDetail(data)
        } else {
            this.update(m => ({
                ...m,
                ...updatedValue,
                data: m.data,
            }));
        }
    }

    initProjectId(id: string) {
        this._projectId = id;
    }

    private _getTechSolutionDetail(projectId: string, query = ''): Promise<IChatItem[]> {
        return this._techSchemeApiService.chatRaw(projectId, 'SECOND', query);
    }

    async getAndOpenSolutionDetail(index: number, projectId: string) {
        const solutionList = this.modelData.data
        const currSolution = _.nth(solutionList, index);
        if (!currSolution) return;

        currSolution.isExpand = !currSolution.isExpand;
        this.update(v => ({ ...v, data: solutionList }));

        if (currSolution.detail || currSolution.loading) return;

        currSolution.loading = true;
        this.update(v => ({ ...v, data: solutionList }));

        const detail = await this._getTechSolutionDetail(projectId, currSolution.point);

        currSolution.detail = detail;
        currSolution.loading = false;
        this.update(v => ({ ...v, data: solutionList }));
    }
}
